<template>
    <div class="mini-overlay" tabindex="0" ref="root">
        <div class="stack-card">
            <div id="container" ref="containerRoot">
                <div id="game" ref="gameContainer"></div>
                <div id="score" ref="score">0</div>
                <div class="game-over" ref="gameOver">
                    <h2>Game Over</h2>
                    <p>You did great, you're the best.</p>
                    <p>Click or spacebar to start again</p>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import * as THREE from 'three';
import { gsap } from 'gsap';

export default {
    name: 'StackGame',
    emits: ['done', 'close'],
    mounted() {
        const isFirefox = /firefox/i.test(navigator.userAgent);

        // Classes ported from the original gist
        const Stage = function (containerEl, options = {}) {
            this.container = containerEl;
            this.isFirefox = !!options.isFirefox;
            this.renderer = new THREE.WebGLRenderer({
                antialias: !this.isFirefox,
                alpha: false,
                powerPreference: 'high-performance'
            });
            this.renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, this.isFirefox ? 1 : 1.5));
            this.renderer.setClearColor('#D0CBC7', 1);
            this.container.appendChild(this.renderer.domElement);
            this.scene = new THREE.Scene();
            let aspect = 1;
            let d = 20;
            this.camera = new THREE.OrthographicCamera(- d * aspect, d * aspect, d, - d, -100, 1000);
            this.camera.position.set(2, 2, 2);
            this.camera.lookAt(new THREE.Vector3(0, 0, 0));
            this.light = new THREE.DirectionalLight(0xffffff, 0.5);
            this.light.position.set(0, 499, 0);
            this.scene.add(this.light);
            this.softLight = new THREE.AmbientLight(0xffffff, 0.4);
            this.scene.add(this.softLight);
            this._onResize = this.onResize.bind(this);
            window.addEventListener('resize', this._onResize);
            this.onResize();
        };
        Stage.prototype.setCamera = function (y, speed = 0.3) {
            gsap.to(this.camera.position, { duration: speed, y: y + 4, ease: "power1.inOut" });
            // camera.lookAt is not an animatable object in three; emulate by adjusting position only
        };
        Stage.prototype.onResize = function () {
            let viewSize = 30;
            let width = this.container.clientWidth || 800;
            let height = this.container.clientHeight || 800;
            let aspect = width / Math.max(height, 1);
            this.renderer.setSize(width, height, false);
            this.camera.left = -viewSize * aspect;
            this.camera.right = viewSize * aspect;
            this.camera.top = viewSize;
            this.camera.bottom = -viewSize;
            this.camera.updateProjectionMatrix();
        };
        Stage.prototype.render = function () {
            this.renderer.render(this.scene, this.camera);
        };
        Stage.prototype.add = function (elem) { this.scene.add(elem); };
        Stage.prototype.remove = function (elem) { this.scene.remove(elem); };
        Stage.prototype.destroy = function () {
            window.removeEventListener('resize', this._onResize);
            this.renderer.dispose();
            if (this.renderer.domElement && this.renderer.domElement.parentNode) {
                this.renderer.domElement.parentNode.removeChild(this.renderer.domElement);
            }
        };

        const Block = function (targetBlock) {
            this.targetBlock = targetBlock;
            this.index = (this.targetBlock ? this.targetBlock.index : 0) + 1;
            this.workingPlane = this.index % 2 ? 'x' : 'z';
            this.workingDimension = this.index % 2 ? 'width' : 'depth';
            this.dimension = {
                width: this.targetBlock ? this.targetBlock.dimension.width : 10,
                height: this.targetBlock ? this.targetBlock.dimension.height : 2,
                depth: this.targetBlock ? this.targetBlock.dimension.depth : 10
            };
            this.position = {
                x: this.targetBlock ? this.targetBlock.position.x : 0,
                y: this.dimension.height * this.index,
                z: this.targetBlock ? this.targetBlock.position.z : 0
            };
            this.colorOffset = this.targetBlock ? this.targetBlock.colorOffset : Math.round(Math.random() * 100);
            if (!this.targetBlock) this.color = 0x333344;
            else {
                let offset = this.index + this.colorOffset;
                var r = Math.sin(0.3 * offset) * 55 + 200;
                var g = Math.sin(0.3 * offset + 2) * 55 + 200;
                var b = Math.sin(0.3 * offset + 4) * 55 + 200;
                this.color = new THREE.Color(r / 255, g / 255, b / 255);
            }
            this.STATES = { ACTIVE: 'active', STOPPED: 'stopped', MISSED: 'missed' };
            this.state = this.index > 1 ? this.STATES.ACTIVE : this.STATES.STOPPED;
            this.MOVE_AMOUNT = 12;
            this.speed = -0.2 - (this.index * 0.02);
            if (this.speed < -4) this.speed = -4;
            this.direction = this.speed;
            let geom = new THREE.BoxGeometry(this.dimension.width, this.dimension.height, this.dimension.depth);
            geom.applyMatrix4(new THREE.Matrix4().makeTranslation(this.dimension.width / 2, this.dimension.height / 2, this.dimension.depth / 2));
            this.material = new THREE.MeshToonMaterial({ color: this.color, flatShading: true });
            this.mesh = new THREE.Mesh(geom, this.material);
            this.mesh.position.set(this.position.x, this.position.y, this.position.z);
            if (this.state === this.STATES.ACTIVE) {
                this.position[this.workingPlane] = Math.random() > 0.5 ? -this.MOVE_AMOUNT : this.MOVE_AMOUNT;
            }
        };
        Block.prototype.reverseDirection = function () { this.direction = this.direction > 0 ? this.speed : Math.abs(this.speed); };
        Block.prototype.place = function () {
            this.state = this.STATES.STOPPED;
            let overlap = this.targetBlock.dimension[this.workingDimension] - Math.abs(this.position[this.workingPlane] - this.targetBlock.position[this.workingPlane]);
            let blocksToReturn = { plane: this.workingPlane, direction: this.direction };
            if (this.dimension[this.workingDimension] - overlap < 0.3) {
                overlap = this.dimension[this.workingDimension];
                blocksToReturn.bonus = true;
                this.position.x = this.targetBlock.position.x;
                this.position.z = this.targetBlock.position.z;
                this.dimension.width = this.targetBlock.dimension.width;
                this.dimension.depth = this.targetBlock.dimension.depth;
            }
            if (overlap > 0) {
                let choppedDimensions = { width: this.dimension.width, height: this.dimension.height, depth: this.dimension.depth };
                choppedDimensions[this.workingDimension] -= overlap;
                this.dimension[this.workingDimension] = overlap;
                let placedGeom = new THREE.BoxGeometry(this.dimension.width, this.dimension.height, this.dimension.depth);
                placedGeom.applyMatrix4(new THREE.Matrix4().makeTranslation(this.dimension.width / 2, this.dimension.height / 2, this.dimension.depth / 2));
                let placedMesh = new THREE.Mesh(placedGeom, this.material);
                let choppedGeom = new THREE.BoxGeometry(choppedDimensions.width, choppedDimensions.height, choppedDimensions.depth);
                choppedGeom.applyMatrix4(new THREE.Matrix4().makeTranslation(choppedDimensions.width / 2, choppedDimensions.height / 2, choppedDimensions.depth / 2));
                let choppedMesh = new THREE.Mesh(choppedGeom, this.material);
                let choppedPosition = { x: this.position.x, y: this.position.y, z: this.position.z };
                if (this.position[this.workingPlane] < this.targetBlock.position[this.workingPlane]) {
                    this.position[this.workingPlane] = this.targetBlock.position[this.workingPlane];
                } else {
                    choppedPosition[this.workingPlane] += overlap;
                }
                placedMesh.position.set(this.position.x, this.position.y, this.position.z);
                choppedMesh.position.set(choppedPosition.x, choppedPosition.y, choppedPosition.z);
                blocksToReturn.placed = placedMesh;
                if (!blocksToReturn.bonus) blocksToReturn.chopped = choppedMesh;
            } else {
                this.state = this.STATES.MISSED;
            }
            this.dimension[this.workingDimension] = overlap;
            return blocksToReturn;
        };
        Block.prototype.tick = function () {
            if (this.state === this.STATES.ACTIVE) {
                let value = this.position[this.workingPlane];
                if (value > this.MOVE_AMOUNT || value < -this.MOVE_AMOUNT) this.reverseDirection();
                this.position[this.workingPlane] += this.direction;
                this.mesh.position[this.workingPlane] = this.position[this.workingPlane];
            }
        };

        // Game class
        const Game = function (els) {
            this.stage = new Stage(els.game, { isFirefox: els.isFirefox });
            this.mainContainer = els.container;
            this.startButton = els.startButton;
            // callback from Vue component
            this.onDone = els.onDone || null;
            this.newBlocks = new THREE.Group();
            this.placedBlocks = new THREE.Group();
            this.choppedBlocks = new THREE.Group();
            this.stage.add(this.newBlocks);
            this.stage.add(this.placedBlocks);
            this.stage.add(this.choppedBlocks);
            this.blocks = [];
            this.addBlock();
            this.tick = this.tick.bind(this);
            this.tick();
            this.updateState('ready');
            this._onKeyDown = (e) => { if (e.code === 'Space') this.onAction(); };
            this._onClick = () => this.onAction();
            this._onTouchStart = (e) => { e.preventDefault(); };
            document.addEventListener('keydown', this._onKeyDown);
            document.addEventListener('click', this._onClick);
            document.addEventListener('touchstart', this._onTouchStart, { passive: false });
        };
        Game.prototype.updateState = function (newState) {
            this.mainContainer.classList.remove('loading', 'playing', 'ready', 'ended', 'resetting');
            this.mainContainer.classList.add(newState);
            this.state = newState;
        };
        Game.prototype.onAction = function () {
            switch (this.state) {
                case 'ready': this.startGame(); break;
                case 'playing': this.placeBlock(); break;
                case 'ended': this.restartGame(); break;
            }
        };
        Game.prototype.startGame = function () {
            if (this.state !== 'playing') {
                this.updateState('playing');
                this.addBlock();
            }
        };
        Game.prototype.restartGame = function () {
            this.updateState('resetting');
            let oldBlocks = this.placedBlocks.children.slice();
            let removeSpeed = 0.2;
            let delayAmount = 0.02;
            for (let i = 0; i < oldBlocks.length; i++) {
                gsap.to(oldBlocks[i].scale, {
                    x: 0, y: 0, z: 0, delay: (oldBlocks.length - i) * delayAmount, duration: removeSpeed, ease: "power1.in",
                    onComplete: () => { this.placedBlocks.remove(oldBlocks[i]); }
                });
                gsap.to(oldBlocks[i].rotation, { y: 0.5, delay: (oldBlocks.length - i) * delayAmount, duration: removeSpeed, ease: "power1.in" });
            }
            let cameraMoveSpeed = removeSpeed * 2 + (oldBlocks.length * delayAmount);
            this.stage.setCamera(2, cameraMoveSpeed);
            let countdown = { value: this.blocks.length - 1 };
            gsap.to(countdown, { value: 0, duration: cameraMoveSpeed });
            this.blocks = this.blocks.slice(0, 1);
            setTimeout(() => { this.startGame(); }, cameraMoveSpeed * 1000);
        };
        Game.prototype.placeBlock = function () {
            let currentBlock = this.blocks[this.blocks.length - 1];
            let newBlocks = currentBlock.place();
            this.newBlocks.remove(currentBlock.mesh);
            if (newBlocks.placed) this.placedBlocks.add(newBlocks.placed);
            if (newBlocks.chopped) {
                let positionParams = { y: '-=30', ease: "power1.in", onComplete: () => this.choppedBlocks.remove(newBlocks.chopped) };
                let rotateParams = { delay: 0.05, x: newBlocks.plane == 'z' ? (Math.random() * 10 - 5) : 0.1, z: newBlocks.plane == 'x' ? (Math.random() * 10 - 5) : 0.1, y: Math.random() * 0.1 };
                if (newBlocks.chopped.position[newBlocks.plane] > newBlocks.placed.position[newBlocks.plane]) positionParams[newBlocks.plane] = '+=' + (40 * Math.abs(newBlocks.direction));
                else positionParams[newBlocks.plane] = '-=' + (40 * Math.abs(newBlocks.direction));
                gsap.to(newBlocks.chopped.position, { duration: 1, ...positionParams });
                gsap.to(newBlocks.chopped.rotation, { duration: 1, ...rotateParams });
            }
            this.addBlock();

            if (this.blocks.length >= 12) {
                this.restartGame();
                if (this.onDone) {
                    this.onDone(this.blocks.length-2);
                }
            }
        };
        Game.prototype.addBlock = function () {
            let lastBlock = this.blocks[this.blocks.length - 1];
            if (lastBlock && lastBlock.state === lastBlock.STATES.MISSED) return this.endGame();
            let newKid = new Block(lastBlock);
            this.newBlocks.add(newKid.mesh);
            this.blocks.push(newKid);
            this.stage.setCamera(this.blocks.length * 2);
        };
        Game.prototype.endGame = function () {
            this.updateState('ended');
        };
        Game.prototype.tick = function () {
            if (this.blocks.length) this.blocks[this.blocks.length - 1].tick();
            this.stage.render();
            this._raf = requestAnimationFrame(this.tick);
        };
        Game.prototype.destroy = function () {
            if (this._raf) cancelAnimationFrame(this._raf);
            document.removeEventListener('keydown', this._onKeyDown);
            document.removeEventListener('click', this._onClick);
            document.removeEventListener('touchstart', this._onTouchStart);
            this.stage.destroy();
        };

        // instantiate game with component refs
        const els = {
            container: this.$refs.containerRoot,
            game: this.$refs.gameContainer,
            score: this.$refs.score,
            startButton: this.$refs.startButton,
            isFirefox,
            onDone: (val) => {
                // update Vue state and emit from component
                this.score = val;
                this.$emit('done', val);
            }
        };
        this._game = new Game(els);
    },
    beforeUnmount() {
        if (this._game) {
            this._game.destroy();
            this._game = null;
        }
    }
};
</script>

<style scoped>
#container {
    width: 100%;
    height: 100%;
    position: relative;
    overflow: hidden;
    font-family: 'Comfortaa', cursive;
}

#score {
    position: absolute;
    top: 20px;
    width: 100%;
    text-align: center;
    font-size: 6vh;
    color: #333344;
    transform: translateY(-200px);
    transition: transform .5s ease;
}

#game {
    position: absolute;
    inset: 0;
}

.game-over {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 85%;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    pointer-events: none;
    opacity: 0;
    transition: opacity .5s, transform .5s;
}

.game-ready {
    position: absolute;
    inset: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    pointer-events: none;
}

#start-button {
    border: 3px solid #333344;
    padding: 10px 20px;
    background: transparent;
    color: #333344;
    font-size: 30px;
    pointer-events: auto;
    cursor: pointer;
}

.container.playing #score {
    transform: translateY(0);
}
</style>